/**
 * sessionStreams.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import React from "react";

import { CircularProgress } from "@mui/material";

import { OTStreamSubscriber } from "./opentokSubscriber.component";

import { SubscriberStream, AspectRatio } from "../models/stream";

interface Props {
  session: OT.Session,
  connecting: boolean,
  isPublishing: boolean,
  screenPublisher: OT.Publisher | null,
  streams: Array<SubscriberStream>,
  //isSharing: boolean,
  availableWidth: number | undefined,
  availableHeight: number | undefined,
  aspectRatio: AspectRatio
}

export function SessionStreamsComponent(props: Props): React.ReactElement<any, any> {

  const [pinnedStream, setPinnedStream] = React.useState<SubscriberStream | null>(null);
  const [speakingStream, setSpeakingStream] = React.useState<SubscriberStream | null>(null);
  const [page, setPage] = React.useState<number>(0);

  const streamCount = props.streams.length + (props.isPublishing ? 1 : 0) + (props.screenPublisher ? 1 : 0);
  const showFocusedStreams = streamCount > 10 || props.screenPublisher || isSomeoneScreenSharing();

  function isSomeoneScreenSharing(): boolean {
    for (const stream of props.streams) {
      if (stream.videoType === "screen") {
        return true;
      }
    }
    return false;
  }

  function speechStarted(stream: SubscriberStream) {
    setSpeakingStream(stream);
  }

  function speechEnded(/*_stream: SubscriberStream*/) {
    for (const stream of props.streams) {
      if (stream.speaking) {
        setSpeakingStream(stream);
        break;
      }
    }
  }
  function clickedStream(stream: SubscriberStream) {
    if (!showFocusedStreams) { return; }
    if (stream.streamId === pinnedStream?.streamId) {
      setPinnedStream(null);
    } else {
      setPinnedStream(stream);
    }
  }

  function prevPage() {
    if (page > 0) { setPage(page -1); }
  }

  function nextPage() {
    setPage(page + 1);
  }

  function areaFits(possibleWidth: number): boolean {
    if (!props.availableWidth || ! props.availableHeight) { return false; }
    const availWidth = props.availableWidth - 60; // 60px for prev/next
    const availHeight = props.availableHeight;
    let totalHeightNeeded: number;

    const streamsPerRow = Math.floor(availWidth / possibleWidth);
    const rows = Math.ceil(streamCount / streamsPerRow);
    if (rows > 0) {
      totalHeightNeeded = rows * (possibleWidth * props.aspectRatio);
    } else {
      // all streams fit in one row only if height fits
      totalHeightNeeded = possibleWidth * props.aspectRatio;
    }

    return totalHeightNeeded < availHeight;
  }

  function resize(): number {
    let max = 0;
    // for each possible width from 1 to 5000
    for (let lower = 50, upper = 5000; lower < upper;) {
      max = Math.floor((upper + lower) / 2);
      if (max === lower) {
        break;
      }
      if (areaFits(max)) {
        lower = max;
      } else {
        upper = max;
      }
    }
    return max;
  }

  function updateStreamState () {
    //let stillSharing = false;
    let stillPinned = false;
    let stillSpeaking = false;
    for (const stream of props.streams) {
      if (pinnedStream?.streamId === stream.streamId) {
        stillPinned = true;
      }
      if (speakingStream?.streamId === stream.streamId) {
        stillSpeaking = true;
      }
      if (stream.videoType === "screen") {
        if (!pinnedStream) {
          setPinnedStream(stream);
        }
        //stillSharing = true;
      }
    }
    /*if (props.screenPublisher) {
      if (!props.isSharing) {
        setSharing(true);
      }
    } else if (props.isSharing && !stillSharing) {
      console.log("no one is screen sharing");
      setSharing(false);
    } else if (!props.isSharing && stillSharing) {
      console.log("someone is screen sharing");
      setSharing(true);
    }*/
    if (pinnedStream && !stillPinned) {
      console.log("pinned stream went away");
      setPinnedStream(null);
    }
    if (speakingStream && !stillSpeaking) {
      console.log("speaking stream went away");
      setSpeakingStream(null);
    }
  }

  let streamWidth: number | undefined = undefined;
  if (showFocusedStreams) {
    streamWidth = 120;
  } else if (props.availableWidth && streamCount > 2) {
    streamWidth = resize();
  }

  updateStreamState();

  let cssName = (props.screenPublisher ? "with-screenshare " : "") +
      (props.isPublishing ? "" : "hide-publisher ") +
      (showFocusedStreams ? "selected-streams" :
       streamCount == 1 ?  "solo" :
       streamCount <= 2 ? "single" :
        "all-streams");
  let haveNext = false;

  switch (props.aspectRatio) {
    case AspectRatio.square: cssName += " square"; break;
    case AspectRatio.widescreen: cssName += " widescreen"; break;
    case AspectRatio.anamorphic: cssName += " anamorphic"; break;
    case AspectRatio.phone: cssName += " phone-aspect"; break;
  }

  function renderAllStreams(): React.ReactElement<any, any> {
    const streamHeight = streamWidth ? streamWidth * props.aspectRatio : undefined;
    return <>
      {props.streams.map(stream => {
        if (!stream.stream) {
          return <div className="subscriber fake" key={stream.streamId}
              style={{width: streamWidth}}>
            <div className="upper-controls">
              <div className="left-side">
                <span className="stream-name">{stream.streamId} and some long text</span>
              </div>
              <div className="right-side">
                <i className="fa-solid fa-video" />
                <i className="fa-solid fa-volume-high" />
              </div>
            </div>
            <div className="subscriber-inner"></div>
          </div>;
        }
        return <OTStreamSubscriber className="subscriber"
              key={stream.streamId}
              session={props.session} stream={stream}
              streamWidth={streamWidth}
              streamHeight={streamHeight}
              onSpeakStart={speechStarted}
              onSpeakEnd={speechEnded}
              style={{width: streamWidth}}
              onClick={clickedStream} />;
      })}
       <div id="screen-share" className="subscriber screen-share" />
    </>;
  }

  function renderStreamsWithFocus(): React.ReactElement<any, any> {
    if (!streamWidth) return <></>; // make TS happy
    const streamHeight = streamWidth * props.aspectRatio;
    const availWidth = (props.availableWidth || 10000) - 60;
    const maxVisibleStreams = Math.min(Math.floor(availWidth / streamWidth), 9);

    let count = 0;
    const start = page * maxVisibleStreams;
    for (const stream of props.streams) {
      if (pinnedStream && stream.streamId === pinnedStream.streamId) {
        stream.hiding = false;
      } else if (!pinnedStream && stream.streamId === speakingStream?.streamId) {
        stream.hiding = false;
        continue;
      } else if (count >= maxVisibleStreams + start) {
        haveNext = true;
        stream.hiding = true;
        continue;
      }
      if (count >= start) {
        stream.hiding = false;
      } else {
        stream.hiding = true;
      }
      count++;
    }

    return <>
      {props.streams.map(stream => {
        const isPinned = props.screenPublisher === null && pinnedStream && stream.streamId === pinnedStream.streamId;
        const isFocused = !pinnedStream && speakingStream?.streamId === stream.streamId;

        const cssProps: React.CSSProperties = (isPinned || isFocused) ?
          {top: streamHeight, height: `calc(100% - ${streamHeight}px)`} :
          {width: streamWidth};

        if (!stream.stream) {
          return <div key={stream.streamId}
              className={"subscriber fake" + (isPinned ? " pinned": (stream.hiding ? " hiding" : ""))}
              style={cssProps}
              onClick={() => clickedStream(stream)}>
            <div className="upper-controls">
              <div className="left-side">
                <span className="stream-name">{stream.name}</span>
              </div>
              <div className="right-side">
                <i className="fa-solid fa-video" />
                <i className="fa-solid fa-volume-high" />
              </div>
            </div>
            <div className="subscriber-inner"></div>
          </div>;
        }
        
        const className = "subscriber" + (isPinned ? " pinned" : 
          (isFocused ? " focused" : 
          (stream.hiding ? " hiding" : "")));

        return <OTStreamSubscriber
          className={className}
          style={cssProps}
          isPinned={isPinned}
          key={stream.streamId}
          streamWidth={streamWidth}
          streamHeight={streamHeight}
          hideVideo={!(isPinned || isFocused) && stream.hiding}
          session={props.session} stream={stream}
          onSpeakStart={speechStarted}
          onSpeakEnd={speechEnded}
          onClick={clickedStream} />
      })}
      <div id="screen-share" className="subscriber screen-share"
        style={{top: streamHeight, height: `calc(100% - ${streamHeight}px)`}} />
    </>;
  }

  return <div className={"streams-container " + cssName}>
      <div className={"prev-container" + (page > 0 ? "" : " disabled") }
          aria-label={"Previous Page"} aria-hidden={!(page > 0)} role="button"
          onClick={() => page > 0 && prevPage()}>
        <i className="fas fa-caret-left"></i>
      </div>

      { props.connecting ? <div className="spinner-container"><CircularProgress size={30} color="secondary" /></div> :
        showFocusedStreams ? renderStreamsWithFocus() : renderAllStreams()}

      <div id="publisher" style={{width: streamWidth}} />
      <div className={"next-container" + (haveNext ? "" : " disabled")}
          aria-label={"Next Page"} aria-hidden={!haveNext} role="button"
          onClick={() => haveNext && nextPage()}>
        <i className="fas fa-caret-right"></i>
      </div>
    </div>;
}
